import { Pause, Plus, Search } from "lucide-react";

import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { formatDate, formatMinutesDuration, getClientTmaTone, getInitials } from "./formatters";
import { PasswordField } from "./password";

export default function AdminClientsView({ portal }) {
  const {
    clients: {
      adminClientForm,
      adminClients,
      clientSearch,
      clientStatusFilter,
      filteredAdminClients,
      handleCreateClient,
      handleEditClient,
      isEditingClient,
      openCreateClientForm,
      openTmaPackageDialog,
      resetClientForm,
      selectedAdminClient,
      setAdminClientForm,
      setClientSearch,
      setClientStatusFilter,
      setIsDeleteClientDialogOpen,
      setShowAdminClientPassword,
      showAdminClientPassword,
    },
    developers: { activeDevelopers },
    ui: { loading },
  } = portal;

  const renderClientWorkspace = () => (
    <div className="support-clients-workspace">
      <aside className="support-clients-list-panel">
        <div className="support-client-filter-row">
          <button type="button" className={clientStatusFilter === "all" ? "is-active" : ""} onClick={() => setClientStatusFilter("all")}>
            Tous <span>{adminClients.length}</span>
          </button>
          <button type="button" className={clientStatusFilter === "active" ? "is-active" : ""} onClick={() => setClientStatusFilter("active")}>
            Actifs <span>{adminClients.filter((client) => client.is_active).length}</span>
          </button>
          <button type="button" className={clientStatusFilter === "inactive" ? "is-active" : ""} onClick={() => setClientStatusFilter("inactive")}>
            Inactifs <span>{adminClients.filter((client) => !client.is_active).length}</span>
          </button>
        </div>

        <div className="support-client-list">
          {filteredAdminClients.map((client) => (
            <button
              key={client.id}
              type="button"
              className={`support-client-list-row ${adminClientForm.id === client.id ? "is-active" : ""}`}
              onClick={() => handleEditClient(client)}
            >
              <span className="support-client-avatar">{getInitials(client.full_name)}</span>
              <span className="support-client-list-main">
                <strong>{client.full_name}</strong>
                <small>{client.email}</small>
              </span>
              <span className={`support-client-tma is-${getClientTmaTone(client)}`}>
                <small>TMA</small>
                {client.tma_enabled
                  ? `${formatMinutesDuration(client.tma_minutes_remaining)} / ${formatMinutesDuration(client.tma_minutes_total)}`
                  : "Inactive"}
              </span>
            </button>
          ))}
          {filteredAdminClients.length === 0 && <p className="body-small">Aucun client a afficher.</p>}
        </div>
      </aside>

      <section className="support-client-editor-panel">
        <div className="support-client-editor-head">
          <div>
            <h3 className="heading-3">{isEditingClient ? "Modifier le client" : "Ajouter un client"}</h3>
            <p className="body-small">
              {isEditingClient
                ? "Modifiez les informations et gérez les forfaits de maintenance."
                : "Créez un accès client avec un premier forfait si besoin."}
            </p>
          </div>
          {isEditingClient && (
            <Button type="button" className="btn-secondary support-danger-btn" onClick={() => setIsDeleteClientDialogOpen(true)} disabled={loading}>
              <Pause />
              Bloquér le client
            </Button>
          )}
        </div>

        <form onSubmit={handleCreateClient} className="support-client-editor-form">
          <section className="support-client-editor-section">
            <h4>Informations generales</h4>
            <div className="support-form-grid">
              <label className="support-field-block">
                <span>Nom du client</span>
                <input className="form-input" type="text" placeholder="Nom du client" value={adminClientForm.full_name} onChange={(e) => setAdminClientForm({ ...adminClientForm, full_name: e.target.value })} required />
              </label>
              <label className="support-field-block">
                <span>Email</span>
                <input className="form-input" type="email" placeholder="Email du client" value={adminClientForm.email} onChange={(e) => setAdminClientForm({ ...adminClientForm, email: e.target.value })} required />
              </label>
              <label className="support-field-block">
                <span>Mot de passe</span>
                <PasswordField
                  value={adminClientForm.password}
                  onChange={(e) => setAdminClientForm({ ...adminClientForm, password: e.target.value })}
                  placeholder={isEditingClient ? "Nouveau mot de passe (optionnel)" : "Mot de passe initial"}
                  required={!isEditingClient}
                  isVisible={showAdminClientPassword}
                  onToggleVisibility={() => setShowAdminClientPassword((current) => !current)}
                />
              </label>
              <label className="support-field-block">
                <span>Développeur référent</span>
                <select className="form-input" value={adminClientForm.managed_developer_id} onChange={(e) => setAdminClientForm({ ...adminClientForm, managed_developer_id: e.target.value })}>
                  <option value="">Aucun développeur référent</option>
                  {activeDevelopers.map((developer) => (
                    <option key={developer.id} value={developer.id}>{developer.full_name}</option>
                  ))}
                </select>
              </label>
            </div>
          </section>

          <section className="support-client-editor-section">
            <div className="support-tma-admin-head">
              <div>
                <h4>Forfaits de maintenance (TMA)</h4>
                <p className="body-small">Ajoutez et gérez les forfaits de maintenance de ce client.</p>
              </div>
              {isEditingClient && (
                <Button type="button" className="btn-primary" onClick={openTmaPackageDialog}>
                  <Plus />
                  Ajouter un forfait
                </Button>
              )}
            </div>

            <label className="support-checkbox-line">
              <input
                type="checkbox"
                checked={adminClientForm.tma_enabled}
                onChange={(e) => setAdminClientForm({
                  ...adminClientForm,
                  tma_enabled: e.target.checked,
                  tma_visible_to_client: e.target.checked ? adminClientForm.tma_visible_to_client : false,
                })}
              />
              Activer les forfaits TMA pour ce client
            </label>

            {adminClientForm.tma_enabled && (
              <>
                {!isEditingClient && (
                  <div className="support-form-grid">
                    <label className="support-field-block">
                      <span>Nom du premier forfait</span>
                      <input className="form-input" type="text" placeholder="Ex: TMA Juin 2026" value={adminClientForm.tma_package_label} onChange={(e) => setAdminClientForm({ ...adminClientForm, tma_package_label: e.target.value })} />
                    </label>
                    <label className="support-field-block">
                      <span>Minutes incluses</span>
                      <input className="form-input" type="number" min="0" step="1" placeholder="600" value={adminClientForm.tma_minutes_total} onChange={(e) => setAdminClientForm({ ...adminClientForm, tma_minutes_total: e.target.value })} />
                    </label>
                  </div>
                )}

                {isEditingClient && (
                  <>
                    <div className="support-tma-admin-table">
                      <div className="support-tma-admin-row is-head">
                        <span>Forfait</span>
                        <span>Heures achetees</span>
                        <span>Statut</span>
                      </div>
                      {(selectedAdminClient?.tma_packages || []).map((item) => (
                        <div key={item.id} className="support-tma-admin-row">
                          <span>
                            <strong>{item.label}</strong>
                            <small>Achete le {formatDate(item.created_at)}</small>
                          </span>
                          <span>{formatMinutesDuration(item.minutes)}</span>
                          <span className="support-status-chip">Actif</span>
                        </div>
                      ))}
                      {!selectedAdminClient?.tma_packages?.length && (
                        <div className="support-tma-admin-empty">Aucun forfait pour le moment.</div>
                      )}
                    </div>
                    <div className="support-tma-total-box">
                      <div>
                        <span>Total restant</span>
                        <strong>{formatMinutesDuration(selectedAdminClient?.tma_minutes_remaining || 0)}</strong>
                      </div>
                      <small>Sur {selectedAdminClient?.tma_packages?.length || 0} forfait(s) actif(s)</small>
                    </div>
                  </>
                )}

                <label className="support-checkbox-line">
                  <input type="checkbox" checked={adminClientForm.tma_visible_to_client} onChange={(e) => setAdminClientForm({ ...adminClientForm, tma_visible_to_client: e.target.checked })} />
                  Afficher les forfaits TMA au client
                </label>
              </>
            )}
          </section>

          <div className="support-client-editor-actions">
            {isEditingClient && (
              <Button type="button" className="btn-secondary support-danger-btn" onClick={() => setIsDeleteClientDialogOpen(true)} disabled={loading}>
                <Pause />
                Bloquér le client
              </Button>
            )}
            <div>
              <Button type="button" className="btn-secondary" onClick={resetClientForm} disabled={loading}>Annuler</Button>
              <Button type="submit" className="btn-primary" disabled={loading}>
                {loading ? (isEditingClient ? "Enregistrement..." : "Création...") : (isEditingClient ? "Enregistrer" : "Créer le client")}
              </Button>
            </div>
          </div>
        </form>
      </section>
    </div>
  );

  return (
    <Card className="support-content-card">
      <CardHeader className="support-clients-card-head">
        <div>
          <CardTitle className="heading-2">Clients</CardTitle>
          <p className="body-small">Gerez vos clients et leurs forfaits de maintenance.</p>
        </div>
        <div className="support-clients-head-actions">
          <Button type="button" className="btn-primary" onClick={openCreateClientForm}>
            <Plus />
            Ajouter un client
          </Button>
          <label className="support-client-search">
            <input
              className="form-input"
              type="search"
              placeholder="Rechercher un client..."
              value={clientSearch}
              onChange={(event) => setClientSearch(event.target.value)}
            />
            <Search aria-hidden="true" />
          </label>
        </div>
      </CardHeader>
      <CardContent className="support-clients-card-content">
        {renderClientWorkspace()}
      </CardContent>
    </Card>
  );
}
